import React, { useState, useRef, forwardRef } from 'react';
import ConstructionTab from './ConstructionTab';
import OperationTab from './OperationTab';
import SummaryTab from './SummaryTab';
import {
  BuildingConstruction,
  CalculatorUrbanDto,
  ConstructionEquipment,
  EnergyConsumption,
  EnergyEfficiency,
  LandUseChange,
  RawMaterialUse,
  RoadMaintenance,
  RoadPavement,
  Streetlights,
} from '../../../../../../services/types/Calculator';
import calculatorService from '../../../../../../services/calculatorService';
import SectorTabContainerBase, {
  SectorTabContainerBaseProps,
  TabContentBase,
  SectorTabContainerBaseHandlers,
  SectorTabContainerBaseHandle,
} from '../Shared/SectorTabContainerBase';

interface Props extends SectorTabContainerBaseProps {
  landUseChange: LandUseChange;
  rawMaterialUse: RawMaterialUse;
  streetlights: Streetlights;
  roadPavement: RoadPavement;
  energyConsumptionConstruction: EnergyConsumption;
  energyConsumptionOperation: EnergyConsumption;
  constructionEquipment: ConstructionEquipment;
  buildingConstruction: BuildingConstruction;
  roadMaintenance: RoadMaintenance;
  energyEfficiency: EnergyEfficiency;
}

const SectorTabContainer = forwardRef<SectorTabContainerBaseHandle, Props>(
  (
    {
      // construction
      landUseChange: initialLandUseChangeProp,
      buildingConstruction: initialBuildingConstructionProp,
      roadPavement: initialRoadPavementProp,
      energyConsumptionConstruction: initialEnergyConsumptionConstructionProp,
      constructionEquipment: initialConstructionEquipmentProp,
      rawMaterialUse: initialRawMaterialUseProp,
      // operation
      streetlights: initialStreetlightsProp,
      roadMaintenance: initialRoadMaintenanceProp,
      energyEfficiency: initialEnergyEfficiencyProp,
      energyConsumptionOperation: initialEnergyConsumptionOperationProp,
      ...baseProps
    }: Props,
    ref
  ) => {
    // construction
    const [landUseChange, setLandUseChange] = useState<LandUseChange>(initialLandUseChangeProp);
    const [buildingConstruction, setBuildingConstruction] = useState<BuildingConstruction>(
      initialBuildingConstructionProp
    );
    const [roadPavement, setRoadPavement] = useState<RoadPavement>(initialRoadPavementProp);
    const [energyConsumptionConstruction, setEnergyConsumptionConstruction] =
      useState<EnergyConsumption>(initialEnergyConsumptionConstructionProp);
    const [constructionEquipment, setConstructionEquipment] = useState<ConstructionEquipment>(
      initialConstructionEquipmentProp
    );
    const [rawMaterialUse, setRawMaterialUse] = useState<RawMaterialUse>(initialRawMaterialUseProp);

    // operations
    const [streetlights, setStreetlights] = useState<Streetlights>(initialStreetlightsProp);
    const [roadMaintenance, setRoadMaintenance] = useState<RoadMaintenance>(
      initialRoadMaintenanceProp
    );
    const [energyEfficiency, setEnergyEfficiency] = useState<EnergyEfficiency>(
      initialEnergyEfficiencyProp
    );

    const [energyConsumptionOperation, setEnergyConsumptionOperation] = useState<EnergyConsumption>(
      initialEnergyConsumptionOperationProp
    );

    // `updateInitialValues()` guarda el "último snapshot guardado" para que
    // hasChanges() lo compare contra el estado actual. ANTES esto reasignaba
    // las variables desestructuradas de los props (initialLandUseChange,
    // etc.) directamente - pero esas son locales de UNA sola invocación de
    // este componente función: la reasignación se pierde apenas React vuelve
    // a renderizar (que pasa siempre, porque el guardado exitoso también
    // dispara setIsSaveDisabled más arriba en SectorTabContainerBase), y el
    // siguiente render las vuelve a inicializar desde los props originales
    // (sin editar). Resultado: hasChanges() quedaba en `true` para siempre
    // después del primer cambio, aunque el guardado hubiera funcionado -
    // isSaveDisabled nunca lograba quedarse en `true`, y por lo tanto
    // saveDataLeaving()/setFinishCalculator() nunca navegaban (bug real
    // encontrado en QA manual tras F4-4). useRef persiste entre renders sin
    // necesidad de re-renderizar cuando cambia, que es exactamente lo que
    // hace falta acá.
    const initialLandUseChangeRef = useRef(initialLandUseChangeProp);
    const initialBuildingConstructionRef = useRef(initialBuildingConstructionProp);
    const initialRoadPavementRef = useRef(initialRoadPavementProp);
    const initialEnergyConsumptionConstructionRef = useRef(
      initialEnergyConsumptionConstructionProp
    );
    const initialConstructionEquipmentRef = useRef(initialConstructionEquipmentProp);
    const initialRawMaterialUseRef = useRef(initialRawMaterialUseProp);
    const initialStreetlightsRef = useRef(initialStreetlightsProp);
    const initialRoadMaintenanceRef = useRef(initialRoadMaintenanceProp);
    const initialEnergyEfficiencyRef = useRef(initialEnergyEfficiencyProp);
    const initialEnergyConsumptionOperationRef = useRef(initialEnergyConsumptionOperationProp);

    // Función para construir el DTO común
    const buildCalculatorUrbanDto = (): CalculatorUrbanDto => ({
      CalculatorId: baseProps.calculatorId,
      // construction
      LandUseChangeRows: landUseChange.rows,
      LandUseTotal: landUseChange.subTotal,
      BuildingConstruction: buildingConstruction,
      BuildingConstructionTotal: buildingConstruction.subTotal,
      RoadPavement: roadPavement,
      RoadPavementTotal: roadPavement.subTotal,
      EnergyConstructionRows: energyConsumptionConstruction.rows,
      EnergyConstructionTotal: energyConsumptionConstruction.subTotal,
      ConstructionEquipment: constructionEquipment,
      ConstructionEquipmentTotal: constructionEquipment.subTotal,
      RawMaterialRows: rawMaterialUse.rows,
      RawMaterialTotal: rawMaterialUse.subTotal,
      // operations
      EnergyOperationRows: energyConsumptionOperation.rows,
      EnergyOperationTotal: energyConsumptionOperation.subTotal,
      Streetlights: streetlights,
      StreetlightsTotal: streetlights.subTotal,
      RoadMaintenance: roadMaintenance,
      RoadMaintenanceTotal: roadMaintenance.subTotal,
      EnergyEfficiency: energyEfficiency,
      EnergyEfficiencyTotal: energyEfficiency.subTotal,
      TotalConstructionOverriden: baseProps.totalConstructionOverriden,
      TotalOperationOverriden: baseProps.totalOperationOverriden,
    });

    const handlers: SectorTabContainerBaseHandlers = {
      buildDto: buildCalculatorUrbanDto,
      saveChanges: async () => {
        const calculatorUrbanDto = buildCalculatorUrbanDto();
        return calculatorService.saveCalculatorUrban(calculatorUrbanDto);
      },
      finishCalculator: async () => {
        const calculatorUrbanDto = buildCalculatorUrbanDto();
        return calculatorService.finishCalculatorUrban(calculatorUrbanDto);
      },
      updateInitialValues: () => {
        initialLandUseChangeRef.current = { ...landUseChange };
        initialBuildingConstructionRef.current = { ...buildingConstruction };
        initialRoadPavementRef.current = { ...roadPavement };
        initialEnergyConsumptionConstructionRef.current = { ...energyConsumptionConstruction };
        initialConstructionEquipmentRef.current = { ...constructionEquipment };
        initialRawMaterialUseRef.current = { ...rawMaterialUse };
        initialEnergyConsumptionOperationRef.current = { ...energyConsumptionOperation };
        initialStreetlightsRef.current = { ...streetlights };
        initialRoadMaintenanceRef.current = { ...roadMaintenance };
        initialEnergyEfficiencyRef.current = { ...energyEfficiency };
      },
      hasChanges: () =>
        JSON.stringify(landUseChange) !== JSON.stringify(initialLandUseChangeRef.current) ||
        JSON.stringify(buildingConstruction) !==
          JSON.stringify(initialBuildingConstructionRef.current) ||
        JSON.stringify(roadPavement) !== JSON.stringify(initialRoadPavementRef.current) ||
        JSON.stringify(energyConsumptionConstruction) !==
          JSON.stringify(initialEnergyConsumptionConstructionRef.current) ||
        JSON.stringify(constructionEquipment) !==
          JSON.stringify(initialConstructionEquipmentRef.current) ||
        JSON.stringify(rawMaterialUse) !== JSON.stringify(initialRawMaterialUseRef.current) ||
        JSON.stringify(streetlights) !== JSON.stringify(initialStreetlightsRef.current) ||
        JSON.stringify(roadMaintenance) !== JSON.stringify(initialRoadMaintenanceRef.current) ||
        JSON.stringify(energyEfficiency) !== JSON.stringify(initialEnergyEfficiencyRef.current) ||
        JSON.stringify(energyConsumptionOperation) !==
          JSON.stringify(initialEnergyConsumptionOperationRef.current),
      checkForNullValues: () =>
        landUseChange.subTotal === null &&
        buildingConstruction.subTotal === null &&
        roadPavement.subTotal === null &&
        energyConsumptionConstruction.subTotal === null &&
        constructionEquipment.subTotal === null &&
        rawMaterialUse.subTotal === null &&
        energyConsumptionOperation.subTotal === null &&
        streetlights.subTotal === null &&
        roadMaintenance.subTotal === null &&
        energyEfficiency.subTotal === null &&
        baseProps.totalConstructionOverriden === null &&
        baseProps.totalOperationOverriden === null,
    };

    const tabContent: TabContentBase = {
      constructionTab: (
        <ConstructionTab
          landUseChange={landUseChange}
          setLandUseChange={setLandUseChange}
          buildingConstruction={buildingConstruction}
          setBuildingConstruction={setBuildingConstruction}
          roadPavement={roadPavement}
          setRoadPavement={setRoadPavement}
          roadMaintenance={roadMaintenance}
          setRoadMaintenance={setRoadMaintenance}
          energyConsumptionConstruction={energyConsumptionConstruction}
          setEnergyConsumptionConstruction={setEnergyConsumptionConstruction}
          constructionEquipment={constructionEquipment}
          setConstructionEquipment={setConstructionEquipment}
          rawMaterialUse={rawMaterialUse}
          setRawMaterialUse={setRawMaterialUse}
        />
      ),
      operationTab: (
        <OperationTab
          streetlights={streetlights}
          setStreetlights={setStreetlights}
          roadMaintenance={roadMaintenance}
          setRoadMaintenance={setRoadMaintenance}
          energyEfficiency={energyEfficiency}
          setEnergyEfficiency={setEnergyEfficiency}
          energyConsumptionOperation={energyConsumptionOperation}
          setEnergyConsumptionOperation={setEnergyConsumptionOperation}
        />
      ),
      summaryTab: (
        <SummaryTab
          landUseChangeTotal={landUseChange.subTotal}
          buildingConstructionTotal={buildingConstruction.subTotal}
          roadPavementTotal={roadPavement.subTotal}
          energyConsumptionConstructionTotal={energyConsumptionConstruction.subTotal}
          construnctionTotal={constructionEquipment.subTotal}
          fuelTotal={constructionEquipment.isFuelData ? constructionEquipment.subTotal : null}
          vehicleTotal={constructionEquipment.isFuelData ? null : constructionEquipment.subTotal}
          co2={constructionEquipment.isFuelData ? null : constructionEquipment.co2Total}
          ch4={constructionEquipment.isFuelData ? null : constructionEquipment.ch4Total}
          n2o={constructionEquipment.isFuelData ? null : constructionEquipment.n2OTotal}
          rawMaterialUseTotal={rawMaterialUse.subTotal}
          energyConsumptionOperationTotal={energyConsumptionOperation.subTotal}
          streetlightsTotal={streetlights.subTotal}
          roadMaintenanceTotal={roadMaintenance.subTotal}
          energyEfficiencyTotal={energyEfficiency.subTotal}
          totalConstructionOverriden={baseProps.totalConstructionOverriden}
          setTotalConstructionOverriden={baseProps.setTotalConstructionOverriden}
          totalOperationOverriden={baseProps.totalOperationOverriden}
          setTotalOperationOverriden={baseProps.setTotalOperationOverriden}
        />
      ),
    };

    return (
      <SectorTabContainerBase
        {...baseProps}
        tabContent={tabContent}
        handlers={handlers}
        ref={ref}
      />
    );
  }
);

export default SectorTabContainer;
